import { ExecutionMethod, Functions, Query } from "appwrite";

import { ID, account, client, tablesDB } from "src/handlers/Appwrite";
import {
  CONNECTORS_TABLE_ID,
  CONNECTOR_ENROLLMENTS_TABLE_ID,
  CONNECTOR_TARGETS_TABLE_ID,
  DEPLOYMENT_JOBS_TABLE_ID,
  DEPLOYMENT_LOGS_TABLE_ID,
  DEVICE_CONFIGS_TABLE_ID,
  DEVICE_INTERFACES_TABLE_ID,
  DEVICE_UPTIME_HISTORY_TABLE_ID,
  DEVICE_RUNTIME_TABLE_ID,
  APPWRITE_DATABASE_ID
} from "src/handlers/Appwrite";
import { getActiveProjectId } from "src/handlers/Projects";
import { listAllRows } from "src/handlers/Appwrite/utils";

const CONNECTOR_CONTROL_FUNCTION_ID =
  import.meta.env.VITE_APPWRITE_CONNECTOR_CONTROL_FUNCTION_ID || "connector-control";

const functions = new Functions(client);
const EXECUTION_POLL_ATTEMPTS = 5;
const EXECUTION_POLL_DELAY_MS = 250;
const FINAL_DEPLOYMENT_JOB_STATUSES = ["succeeded", "failed", "cancelled"];

function parseExecutionResponse(execution) {
  const body = execution?.responseBody || "";

  if (!body) return null;

  try {
    return JSON.parse(body);
  } catch {
    return {
      ok: false,
      error: body
    };
  }
}

async function executeConnectorControl(xpath, payload) {
  const jwt = await account.createJWT();
  let execution = await functions.createExecution({
    functionId: CONNECTOR_CONTROL_FUNCTION_ID,
    body: JSON.stringify({
      ...(payload ?? {}),
      jwt: jwt.jwt
    }),
    async: false,
    xpath,
    method: ExecutionMethod.POST,
    headers: {
      "content-type": "application/json"
    }
  });

  for (let attempt = 0; attempt < EXECUTION_POLL_ATTEMPTS; attempt += 1) {
    if (execution?.responseBody) break;

    await new Promise((resolve) => {
      window.setTimeout(resolve, EXECUTION_POLL_DELAY_MS);
    });

    execution = await functions.getExecution({
      functionId: CONNECTOR_CONTROL_FUNCTION_ID,
      executionId: execution.$id
    });
  }

  const response = parseExecutionResponse(execution);

  if (!response?.ok) {
    throw new Error(
      response?.error ||
        execution?.responseBody ||
        `Connector control request failed (status ${execution?.responseStatusCode || "unknown"}).`
    );
  }

  return response;
}

function getProjectId(projectId) {
  return projectId || getActiveProjectId() || "";
}

export async function createConnectorEnrollment({ projectId, name, site, expiresInMinutes }) {
  return executeConnectorControl("/enrollment/create", {
    projectId: getProjectId(projectId),
    name,
    site,
    expiresInMinutes
  });
}

export async function upsertConnectorTarget({
  projectId,
  connectorId,
  deviceKey,
  deviceType,
  hostname,
  mgmtIp,
  username,
  authType,
  password,
  privateKey,
  enableSecret,
  port,
  enabled,
  hostKey,
  applyToAllConnectors
}) {
  return executeConnectorControl("/targets/upsert", {
    projectId: getProjectId(projectId),
    connectorId,
    deviceKey,
    deviceType,
    hostname,
    mgmtIp,
    username,
    authType,
    password,
    privateKey,
    enableSecret,
    port,
    enabled,
    hostKey,
    applyToAllConnectors
  });
}

export async function deleteConnectorTarget({ projectId, connectorId, deviceKey }) {
  return executeConnectorControl("/targets/delete", {
    projectId: getProjectId(projectId),
    connectorId,
    deviceKey
  });
}

export async function deleteConnector({ projectId, connectorId }) {
  return executeConnectorControl("/connectors/delete", {
    projectId: getProjectId(projectId),
    connectorId
  });
}

export async function createDeploymentJob({ projectId, connectorId, deviceKey, mode, payload }) {
  return executeConnectorControl("/jobs/create", {
    projectId: getProjectId(projectId),
    connectorId,
    deviceKey,
    mode,
    payload
  });
}

export async function createTerminalSession({ projectId, connectorId, deviceKey, payload }) {
  return executeConnectorControl("/terminal/session/create", {
    projectId: getProjectId(projectId),
    connectorId,
    deviceKey,
    payload
  });
}

export async function sendTerminalSessionInput({ projectId, sessionId, input }) {
  return executeConnectorControl("/terminal/session/input", {
    projectId: getProjectId(projectId),
    sessionId,
    input
  });
}

export async function appendTerminalSessionInput({
  projectId = getProjectId(),
  sessionId,
  connectorId,
  deviceKey,
  input,
  teamId
}) {
  const serializedInput =
    typeof input === "string" ? input : JSON.stringify(input ?? {});

  if (!sessionId || !connectorId || !deviceKey || !String(serializedInput || "").length) {
    throw new Error("Missing terminal input payload.");
  }

  const permissions = teamId
    ? [
        `read("team:${teamId}")`,
        `update("team:${teamId}")`
      ]
    : undefined;

  return tablesDB.createRow({
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEPLOYMENT_LOGS_TABLE_ID,
    rowId: ID.unique(),
    data: {
      projectId,
      jobId: sessionId,
      connectorId,
      deviceKey,
      level: "input",
      message: serializedInput,
      timestampMs: Date.now()
    },
    permissions
  });
}

export async function closeTerminalSession({ projectId, sessionId }) {
  return executeConnectorControl("/terminal/session/close", {
    projectId: getProjectId(projectId),
    sessionId
  });
}

export async function getConnectorSummary({ projectId } = {}) {
  const result = await executeConnectorControl("/summary", {
    projectId: getProjectId(projectId)
  });
  try {
    const hasAny = Array.isArray(result?.connectors) && result.connectors.length > 0;
    const pid = getProjectId(projectId);
    localStorage.setItem("hasConnectors", hasAny ? "1" : "0");
    if (pid) localStorage.setItem(`hasConnectors_${pid}`, hasAny ? "1" : "0");
    window.dispatchEvent(new CustomEvent("hasConnectorsChanged", { detail: hasAny }));
  } catch {
    // ignore storage errors
  }
  return result;
}

export async function listConnectors(projectId = getProjectId()) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: CONNECTORS_TABLE_ID,
    queries: [Query.equal("projectId", [projectId])]
  });
}

export async function listConnectorEnrollments(projectId = getProjectId()) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: CONNECTOR_ENROLLMENTS_TABLE_ID,
    queries: [Query.equal("projectId", [projectId])]
  });
}

export async function listDeviceRuntime(projectId = getProjectId()) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEVICE_RUNTIME_TABLE_ID,
    queries: [Query.equal("projectId", [projectId])]
  });
}

export async function listConnectorTargets({
  projectId = getProjectId(),
  connectorId
}) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: CONNECTOR_TARGETS_TABLE_ID,
    queries: [
      Query.equal("projectId", [projectId]),
      ...(connectorId ? [Query.equal("connectorId", [connectorId])] : [])
    ]
  });
}

export async function listDeviceInterfaces({ projectId = getProjectId(), deviceKey }) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEVICE_INTERFACES_TABLE_ID,
    queries: [Query.equal("projectId", [projectId]), Query.equal("deviceKey", [deviceKey])]
  });
}

export async function listDeviceConfigs({ projectId = getProjectId(), deviceKey }) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEVICE_CONFIGS_TABLE_ID,
    queries: [Query.equal("projectId", [projectId]), Query.equal("deviceKey", [deviceKey])]
  });
}

/**
 * Fetch the most recent running-config uploaded by a connector for a given
 * device. Used by the DeploymentReviewModal to show a diff against what's
 * actually on the box right now (Phase C6).
 *
 * @param {{ projectId?: string, deviceKey: string }} args
 * @returns {Promise<{ content: string, collectedAtMs: number } | null>}
 */
export async function getLatestRunningConfig({ projectId = getProjectId(), deviceKey }) {
  if (!projectId || !deviceKey) return null;

  try {
    const rows = await listAllRows(tablesDB, {
      databaseId: APPWRITE_DATABASE_ID,
      tableId: DEVICE_CONFIGS_TABLE_ID,
      queries: [
        Query.equal("projectId", [projectId]),
        Query.equal("deviceKey", [deviceKey]),
        Query.equal("configType", ["running"]),
        Query.orderDesc("collectedAtMs"),
        Query.limit(1)
      ]
    });

    const row = rows?.[0];
    if (!row || !row.content) return null;
    return {
      content: String(row.content),
      collectedAtMs: Number(row.collectedAtMs || 0)
    };
  } catch {
    return null;
  }
}

export async function listDeviceUptimeHistory({ projectId = getProjectId(), deviceKey }) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEVICE_UPTIME_HISTORY_TABLE_ID,
    queries: [Query.equal("projectId", [projectId]), Query.equal("deviceKey", [deviceKey])]
  });
}

export async function listDeploymentJobs(projectId = getProjectId()) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEPLOYMENT_JOBS_TABLE_ID,
    queries: [Query.equal("projectId", [projectId])]
  });
}

export async function getLastSuccessfulDeployConfig({ projectId = getProjectId(), deviceKey }) {
  if (!projectId || !deviceKey) return { renderedConfig: null, tabData: null };

  const rows = await listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEPLOYMENT_JOBS_TABLE_ID,
    queries: [
      Query.equal("projectId", [projectId]),
      Query.equal("deviceKey", [deviceKey]),
      Query.equal("status", ["succeeded"]),
      Query.equal("mode", ["merge"]),
      Query.orderDesc("createdAtMs"),
      Query.limit(1)
    ]
  });

  const job = rows?.[0];
  if (!job) return { renderedConfig: null, tabData: null };

  try {
    const payload = typeof job.payloadJson === "string" ? JSON.parse(job.payloadJson) : job.payloadJson;
    return {
      renderedConfig: payload?.renderedConfig || null,
      tabData: payload?.tabData || null
    };
  } catch {
    return { renderedConfig: null, tabData: null };
  }
}

export async function listDeploymentLogs({ projectId = getProjectId(), jobId }) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEPLOYMENT_LOGS_TABLE_ID,
    queries: [Query.equal("projectId", [projectId]), Query.equal("jobId", [jobId])]
  });
}

export async function getDeploymentJob({ projectId = getProjectId(), jobId }) {
  const row = await tablesDB.getRow({
    databaseId: APPWRITE_DATABASE_ID,
    tableId: DEPLOYMENT_JOBS_TABLE_ID,
    rowId: jobId
  });

  if (projectId && row?.projectId && row.projectId !== projectId) {
    throw new Error("Deployment job does not belong to the active project.");
  }

  return row;
}

export function subscribeDeploymentJob(jobId, onUpdate) {
  if (!jobId || typeof client.subscribe !== "function") {
    return () => {};
  }

  const channel = `databases.${APPWRITE_DATABASE_ID}.tables.${DEPLOYMENT_JOBS_TABLE_ID}.rows.${jobId}`;
  return client.subscribe(channel, (response) => {
    const row = response?.payload;
    if (!row || row.$id !== jobId) return;
    onUpdate?.(row, response);
  });
}

export function subscribeDeploymentLogs(jobId, onUpdate) {
  if (!jobId || typeof client.subscribe !== "function") {
    return () => {};
  }

  const channel = `databases.${APPWRITE_DATABASE_ID}.tables.${DEPLOYMENT_LOGS_TABLE_ID}.rows`;
  return client.subscribe(channel, (response) => {
    const row = response?.payload;
    if (!row || row.jobId !== jobId) return;
    onUpdate?.(row, response);
  });
}

export async function waitForDeploymentJob({
  projectId = getProjectId(),
  jobId,
  timeoutMs = 45000,
  pollIntervalMs = 750,
  onUpdate
}) {
  if (!jobId) {
    throw new Error("Missing deployment job id.");
  }

  const startedAt = Date.now();

  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;
    let pollTimeoutId = null;
    let unsubscribe = null;

    const cleanup = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (pollTimeoutId) window.clearTimeout(pollTimeoutId);
      if (typeof unsubscribe === "function") unsubscribe();
    };

    const finish = (callback) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };

    const maybeResolve = (row) => {
      if (!row) return false;
      onUpdate?.(row);
      if (!FINAL_DEPLOYMENT_JOB_STATUSES.includes(row.status)) {
        return false;
      }

      finish(() => resolve(row));
      return true;
    };

    const poll = async () => {
      if (settled) return;

      try {
        const row = await getDeploymentJob({ projectId, jobId });
        if (maybeResolve(row)) return;
      } catch (error) {
        finish(() => reject(error));
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        finish(() => reject(new Error("Timed out while waiting for deployment job.")));
        return;
      }

      pollTimeoutId = window.setTimeout(poll, pollIntervalMs);
    };

    try {
      unsubscribe = subscribeDeploymentJob(jobId, (row) => {
        maybeResolve(row);
      });
    } catch {
      unsubscribe = null;
    }

    timeoutId = window.setTimeout(() => {
      finish(() => reject(new Error("Timed out while waiting for deployment job.")));
    }, timeoutMs);

    poll();
  });
}
